import { _ } from "libs";
import React, { useState, useEffect, forwardRef, useRef, useImperativeHandle } from "react";
import withRedux from "../../withRedux";
import { ClubAutocompleteResult, ClubMembershipData } from "../../types";
import { requestClubMembership, cancelClubMembershipRequest, loadMyClubMemberships } from "../../actions";
import ClubInput from "../../components/onboarding/clubInput";
import ClubItemMedium from "../../components/items/club/medium";
import Loader from "../../components/loader";
import { Link } from "react-router-dom";
import AsyncButton from "../../components/asyncButton";

type CancelRequest = (clubId: string) => Promise<any>;

type Props = {
	requestClubMembership: (clubId: string) => Promise<any>;
	cancelClubMembershipRequest: CancelRequest;
};

const MyClubMembershipsList = forwardRef(
	({ cancelClubMembershipRequest }: { cancelClubMembershipRequest: CancelRequest }, ref) => {
		const [memberships, setMemberships] = useState(null as ClubMembershipData[]);
		const unmounted = useRef(false);

		const reload = () => {
			loadMyClubMemberships().then(setMemberships);
		};

		useEffect(() => {
			if (!unmounted.current) {
				reload();
			}

			return () => {
				unmounted.current = true;
			};
		}, []);

		useImperativeHandle(ref, () => ({
			reload: () => {
				reload();
			},
		}));

		if (memberships === null) {
			return <Loader />;
		}

		return (
			<div className="table memberships defaultSpacing">
				{memberships.map((club, key) => {
					return (
						<div className="cell standalone" key={key} id={club.clubInfo.clubId}>
							<Link to={`/club/${club.clubInfo.clubId}`} style={{ width: "64%" }}>
								<ClubItemMedium
									name={club.clubInfo.name}
									meta={club.clubInfo.city}
									photoUrl={club.clubInfo.photoUrl}
								/>
							</Link>
							<div className="itemExtra">
								{club.clubDetails.memberStatus === "member" ? (
									<div className="attributeBadge turquoise checkmark">{_("membership.active")}</div>
								) : (
									<>
										<div className="attributeBadge grey hourglass">{_("membership.pending")}</div>
										<AsyncButton
											className="iconButton cancel"
											question={_("membership.cancelRequestConfirmation")}
											onClick={async () => {
												await cancelClubMembershipRequest(club.clubInfo.clubId);
											}}
											callback={reload}
										/>
									</>
								)}
							</div>
						</div>
					);
				})}
			</div>
		);
	}
);
MyClubMembershipsList.displayName = "MyClubMembershipsList";

const ClubClaimer = ({ club, claimHandler }: { club: ClubAutocompleteResult; claimHandler }) => {
	const handler = () => {
		claimHandler(club);
	};

	return (
		<div className="cell claimClub">
			<ClubItemMedium name={club.name} meta={club.city} photoUrl={club.photoUrl} />
			<div className="horizontal">
				<div className="">
					<button className="button small done" onClick={handler}>
						{_("membershipForm.claimBtn")}
					</button>
				</div>
				<div className="">
					<button className="button small done" onClick={handler}>
						{_("membershipForm.requestBtn")}
					</button>
				</div>
			</div>
		</div>
	);
};

const Memberships = ({ requestClubMembership, cancelClubMembershipRequest }: Props) => {
	const [selectedClub, selectClub] = useState(null);
	const list = useRef<typeof MyClubMembershipsList>();
	const input = useRef<typeof ClubInput>();

	const claimHandler = (club: ClubAutocompleteResult) => {
		requestClubMembership(club.clubId).then(() => {
			selectClub(null);
			if (list.current) {
				//@ts-ignore
				list.current.reload();
			}
			if (input.current) {
				//@ts-ignore
				input.current.clear();
			}
		});
	};

	return (
		<>
			<h5>{_("myMemberships.heading")}</h5>
			<MyClubMembershipsList ref={list} cancelClubMembershipRequest={cancelClubMembershipRequest} />
			<h5>{_("claimOrGetMembership.heading")}</h5>
			<div className="form">
				<div className="field searchField searchFieldWithIcon">
					<label>{_("club")}</label>
					<ClubInput onSelect={selectClub} ref={input} replaceValueOnSelect={false} />
				</div>
			</div>
			{selectedClub ? <ClubClaimer club={selectedClub} claimHandler={claimHandler} /> : null}
		</>
	);
};

export default withRedux({ requestClubMembership, cancelClubMembershipRequest }, () => {
	return {};
})(Memberships);
